import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Download } from 'lucide-react';
import toast from 'react-hot-toast';
import userService from '../services/userService';
import SearchableUserSelect from '../components/SearchableUserSelect';
import LoadingSpinner from '../components/LoadingSpinner';

const TABS = [
  { id: 'logins', label: 'Logowania' },
  { id: 'sessions', label: 'Sesje' },
  { id: 'actions', label: 'Czynności' }
];

function todayInputValue() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

function daysAgoInputValue(days) {
  const date = new Date();
  date.setDate(date.getDate() - days);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function formatWhen(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('pl-PL');
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

const UserActivity = () => {
  const [userId, setUserId] = useState('');
  const [from, setFrom] = useState(daysAgoInputValue(30));
  const [to, setTo] = useState(todayInputValue());
  const [submitted, setSubmitted] = useState(null);
  const [tab, setTab] = useState('logins');
  const [exporting, setExporting] = useState('');

  const { data: usersData, isLoading: usersLoading } = useQuery('users', userService.getUsers);

  const { data: report, isLoading, isFetching, error } = useQuery(
    ['userActivity', submitted],
    () => userService.getUserActivity(submitted.userId, submitted.from, submitted.to),
    { enabled: Boolean(submitted), keepPreviousData: true }
  );

  const users = usersData?.users || [];
  const counts = report?.counts;

  const rows = useMemo(() => {
    if (!report) return [];
    if (tab === 'sessions') return report.sessions || [];
    if (tab === 'actions') return report.actions || [];
    return report.logins || [];
  }, [report, tab]);

  const showReport = (event) => {
    event.preventDefault();
    if (!userId) {
      toast.error('Wybierz konto');
      return;
    }
    if (!from || !to || from > to) {
      toast.error('Podaj poprawny zakres dat');
      return;
    }
    setSubmitted({ userId, from, to });
  };

  const exportFile = async (format) => {
    if (!submitted) return;
    setExporting(format);
    try {
      const { blob, filename } = await userService.downloadUserActivity(
        submitted.userId,
        submitted.from,
        submitted.to,
        format
      );
      downloadBlob(blob, filename);
    } catch (exportError) {
      toast.error(exportError.message || 'Nie udało się pobrać pliku');
    } finally {
      setExporting('');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title">Aktywność konta</h1>
        <p className="page-subtitle">
          Logowania, sesje i zmiany danych jednego użytkownika. Historia logowań i dziennik czynności obejmują zdarzenia od 12 października 2026. Starsze sesje są widoczne, jeśli nadal są w bazie.
        </p>
      </div>

      <form onSubmit={showReport} className="card p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-semibold text-ink mb-1.5">Konto</label>
            {usersLoading ? (
              <div className="input text-ink-faint">Wczytywanie kont…</div>
            ) : (
              <SearchableUserSelect
                users={users}
                value={userId}
                onChange={setUserId}
                placeholder="— Wybierz konto —"
                clearLabel="— Wybierz konto —"
              />
            )}
          </div>
          <div>
            <label className="block text-sm font-semibold text-ink mb-1.5" htmlFor="activity-from">Od</label>
            <input id="activity-from" type="date" className="input" value={from} onChange={(event) => setFrom(event.target.value)} required />
          </div>
          <div>
            <label className="block text-sm font-semibold text-ink mb-1.5" htmlFor="activity-to">Do</label>
            <input id="activity-to" type="date" className="input" value={to} onChange={(event) => setTo(event.target.value)} required />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="submit" className="btn-primary" disabled={isFetching}>
            {isFetching ? 'Pobieranie…' : 'Pokaż'}
          </button>
          <button
            type="button"
            className="btn-secondary"
            disabled={!report || exporting !== ''}
            onClick={() => exportFile('csv')}
          >
            <Download className="h-4 w-4 mr-2" />
            {exporting === 'csv' ? 'Przygotowywanie…' : 'Pobierz CSV'}
          </button>
          <button
            type="button"
            className="btn-secondary"
            disabled={!report || exporting !== ''}
            onClick={() => exportFile('json')}
          >
            <Download className="h-4 w-4 mr-2" />
            {exporting === 'json' ? 'Przygotowywanie…' : 'Pobierz JSON'}
          </button>
        </div>
      </form>

      {isLoading && <LoadingSpinner />}

      {error && (
        <div className="card p-5 text-sm text-red-700">
          {error.response?.data?.message || error.message || 'Nie udało się pobrać aktywności'}
        </div>
      )}

      {report && !isLoading && (
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-ink">
                {report.user.firstName || report.user.lastName
                  ? `${report.user.firstName || ''} ${report.user.lastName || ''}`.trim()
                  : report.user.username}
              </p>
              <p className="text-xs text-ink-faint">{report.user.email} · {report.from} – {report.to}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {TABS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setTab(item.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold ${
                    tab === item.id ? 'bg-primary-600 text-white' : 'bg-surface text-ink-soft'
                  }`}
                >
                  {item.label} ({counts?.[item.id] ?? 0})
                </button>
              ))}
            </div>
          </div>

          {rows.length === 0 ? (
            <p className="px-5 py-10 text-sm text-ink-faint text-center">Brak wpisów w tym zakresie.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-[11px] font-bold uppercase tracking-wide text-ink-faint">
                    {tab === 'logins' && (
                      <>
                        <th className="px-4 py-3">Czas</th>
                        <th className="px-3 py-3">Zdarzenie</th>
                        <th className="px-3 py-3">Wynik</th>
                        <th className="px-3 py-3">Adres IP</th>
                        <th className="px-4 py-3">Przeglądarka</th>
                      </>
                    )}
                    {tab === 'sessions' && (
                      <>
                        <th className="px-4 py-3">Logowanie</th>
                        <th className="px-3 py-3">Zakończenie</th>
                        <th className="px-3 py-3">Ostatnia aktywność</th>
                        <th className="px-3 py-3">Status</th>
                        <th className="px-3 py-3">Adres IP</th>
                        <th className="px-4 py-3">Przeglądarka</th>
                      </>
                    )}
                    {tab === 'actions' && (
                      <>
                        <th className="px-4 py-3">Czas</th>
                        <th className="px-3 py-3">Operacja</th>
                        <th className="px-3 py-3">Metoda</th>
                        <th className="px-3 py-3">Ścieżka</th>
                        <th className="px-3 py-3">Rekord</th>
                        <th className="px-4 py-3">Status</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {tab === 'logins' && rows.map((row) => (
                    <tr key={row.id} className="hover:bg-surface/60">
                      <td className="px-4 py-3 whitespace-nowrap text-ink">{formatWhen(row.at)}</td>
                      <td className="px-3 py-3 text-ink">
                        {row.description}
                        {row.impersonatedByUsername ? ` (${row.impersonatedByUsername})` : ''}
                      </td>
                      <td className="px-3 py-3 text-ink-soft">{row.resultLabel}</td>
                      <td className="px-3 py-3 text-ink-soft whitespace-nowrap">{row.ipAddress || '—'}</td>
                      <td className="px-4 py-3 text-ink-faint max-w-xs truncate">{row.userAgent || '—'}</td>
                    </tr>
                  ))}
                  {tab === 'sessions' && rows.map((row) => (
                    <tr key={row.id} className="hover:bg-surface/60">
                      <td className="px-4 py-3 whitespace-nowrap text-ink">{formatWhen(row.loginTime)}</td>
                      <td className="px-3 py-3 whitespace-nowrap text-ink-soft">{formatWhen(row.logoutTime)}</td>
                      <td className="px-3 py-3 whitespace-nowrap text-ink-soft">{formatWhen(row.lastActivity)}</td>
                      <td className="px-3 py-3 text-ink-soft">{row.statusLabel}</td>
                      <td className="px-3 py-3 text-ink-soft whitespace-nowrap">{row.ipAddress || '—'}</td>
                      <td className="px-4 py-3 text-ink-faint max-w-xs truncate">{row.userAgent || '—'}</td>
                    </tr>
                  ))}
                  {tab === 'actions' && rows.map((row) => (
                    <tr key={row.id} className="hover:bg-surface/60">
                      <td className="px-4 py-3 whitespace-nowrap text-ink">{formatWhen(row.at)}</td>
                      <td className="px-3 py-3 text-ink">{row.actionLabel}</td>
                      <td className="px-3 py-3 text-ink-soft">{row.method}</td>
                      <td className="px-3 py-3 text-ink-soft max-w-sm truncate">{row.path}</td>
                      <td className="px-3 py-3 text-ink-faint">{row.resourceId || '—'}</td>
                      <td className="px-4 py-3 text-ink-soft">{row.statusCode}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserActivity;
